import {
  type ActionFunctionArgs,
  isRouteErrorResponse,
  json,
  type LoaderFunctionArgs,
  redirect,
  useRouteError,
} from 'react-router-dom'
import { type QueryClient, useQuery } from '@tanstack/react-query'
import { type AddUserData, isZodError } from 'api-client'
import { type DeleteProjectData } from 'api-client/projects/model'
import { authorize } from 'keycloak'
import { z } from 'zod'

import { AddUserToProject } from '~/features/addUserToProject'
import { notify } from '~/features/notifications'

import { integrationsQuery } from '~/entities/integrations/base'
import { organizationsUsersQuery } from '~/entities/organizations'
import {
  ADD_USER_TO_PROJECT,
  checkProjectSlugParam,
  DELETE_PROJECT,
  getProjectIdFromParams,
  projectsQuery,
  useProjectIdParam,
  UsersFromProject,
  usersFromProjectQuery,
} from '~/entities/projects'
import { DeleteProject } from '~/entities/projects/ui/DeleteProject'

import { apiClient, handleDataMutationError } from '~/shared/api'
import { getFormData } from '~/shared/lib/parseRequest'
import { authStore } from '~/shared/model/auth'
import { isApiError } from '~/shared/model/errors'
import { RouteErrorMessage } from '~/shared/ui/RouteErrorMessage'

import { Box } from '~/components/Box'
import { HeadTag } from '~/components/HeadTag'
import { PageHeader } from '~/components/PageHeader'
import { Stack } from '~/components/Stack'

export function loader(queryClient: QueryClient) {
  return async ({ params }: LoaderFunctionArgs) =>
    authorize(authStore, async () => {
      const { projectId } = checkProjectSlugParam(params)
      const decodedToken = authStore.getState()?.decodedToken

      try {
        const [projects, orgUsers, projectUsers] = await Promise.all([
          queryClient.ensureQueryData(projectsQuery()),
          decodedToken?.organization_id
            ? queryClient.ensureQueryData(organizationsUsersQuery(true))
            : undefined,
          decodedToken?.organization_id
            ? queryClient.ensureQueryData(usersFromProjectQuery(projectId, true))
            : undefined,
        ])

        return json({ orgUsers, projectUsers, projects })
      } catch (error) {
        if (isApiError(error) && error.type === 'data_query') {
          throw json(
            {
              message: `We had issues loading the settings information for project ${projectId}`,
              description: `Please refresh the page and try again. If the problem persists, please contact support.`,
            },
            { status: 400 },
          )
        }
        throw error
      }
    })
}

export function action(queryClient: QueryClient) {
  return async ({ request, params }: ActionFunctionArgs) => {
    const data = await getFormData(request)
    const projectId = getProjectIdFromParams(params)
    if (!projectId) {
      notify.error('No project has been selected.')
      return json({})
    }

    const intents = z.enum([ADD_USER_TO_PROJECT, DELETE_PROJECT]).safeParse(data.intent)
    if (!intents.success) {
      notify.error('Invalid intent.')
      return json({})
    }

    switch (intents.data) {
      case DELETE_PROJECT: {
        try {
          const { message } = await apiClient.projects.deleteProject(data.id)
          await queryClient.invalidateQueries(usersFromProjectQuery(data.id, true))

          notify.success(message)
          // navigate to the projects page
          return redirect('/projects')
        } catch (error) {
          if (isZodError<DeleteProjectData>(error)) {
            return json({ fields: data, errors: error.flatten() }, { status: 400 })
          }
          return handleDataMutationError(error)
        }
      }

      case ADD_USER_TO_PROJECT: {
        try {
          const { message } = await apiClient.projects.addUserToProject({
            ...data,
            projectId: projectId,
          })
          notify.success(message)

          await queryClient.invalidateQueries(usersFromProjectQuery(projectId, true))

          return json({})
        } catch (error) {
          if (isZodError<AddUserData>(error)) {
            return json({ fields: data, errors: error.flatten() }, { status: 400 })
          }
          return handleDataMutationError(error)
        }
      }
    }
  }
}

export default function Settings() {
  const { projectId } = checkProjectSlugParam(useProjectIdParam())
  const decodedToken = authStore.getState()?.decodedToken
  const isOrgAccount = Boolean(decodedToken?.organization_id)

  const { data: projects } = useQuery(projectsQuery())

  const currentProjectName = projects?.find(project => project.id === projectId)?.name

  // check if the project has hepic integration
  const { data: hasHepicActive } = useQuery({
    ...integrationsQuery(projectId),
    select: data =>
      data.some(
        integration => integration.product.slug === 'hepic' && integration.status === 'active',
      ),
  })

  // check if current user is the owner of the organization
  const { data: isOrgOwner } = useQuery({
    ...organizationsUsersQuery(isOrgAccount),
    select: data => data.find(user => user.owner)?.email === decodedToken?.email,
  })

  return (
    <>
      <HeadTag tag="title" headId="title">
        {import.meta.env.MODE !== 'production' ? `(${import.meta.env.VITE_ENVIRONMENT})` : ''}
        Project settings - Gigapipe
      </HeadTag>

      <Box paddingX={{ mobile: 'xxsmall', tablet: 'large', desktop: 'xxlarge' }}>
        <Stack>
          <PageHeader title="Project Settings" />

          <UsersFromProject />
          <AddUserToProject />

          <DeleteProject
            projectId={projectId}
            projectSlug={currentProjectName ?? ''}
            isOrgAccount={isOrgAccount ?? false}
            isProjectOwner={isOrgOwner ?? false}
            isHepicActive={hasHepicActive ?? false}
          />
        </Stack>
      </Box>
    </>
  )
}

export function ErrorBoundary() {
  const error = useRouteError() as Error

  if (isRouteErrorResponse(error)) {
    return (
      <RouteErrorMessage
        message={error.data.message}
        description={error.data.description}
        goToPath={error.data.goTo?.path}
        goToText={error.data.goTo?.text}
      />
    )
  }

  throw error
}
